/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useProductDetails } from '@/features/product/api/getProductDetails';
import type {
  GetReviewOptions,
  ReviewsResponseType,
  ReviewType,
  SortAttributeType,
  ReviewMediaItem,
} from '../interface/types';
import { prefetchReviews, useGetReviews } from '../api/getReviews';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';

export type ReviewPhotoMap = {
  [key: string]: string[] | undefined;
};

type ReviewFilterParams = {
  showReviewsRated1Star?: boolean;
  showReviewsRated2Star?: boolean;
  showReviewsRated3Star?: boolean;
  showReviewsRated4Star?: boolean;
  showReviewsRated5Star?: boolean;
  showReviewsFromVerifiedPurchasers?: boolean;
  sortAttribute?: SortAttributeType;
  searchText?: string;
  showReviewsWithImagesOnly?: boolean;
};

export type ReviewImageResult = {
  reviews: ReviewType[] | null;
  mediaItems: ReviewMediaItem[];
  imageUrls: string[] | undefined;
  photoMap: ReviewPhotoMap;
};

export function useProductReviewImages(
  filterParams?: ReviewFilterParams,
): ReviewImageResult | undefined {
  const { data: productDetails } = useProductDetails();
  const skuId = productDetails?.product?.itemId ?? '';

  const { data } = useGetReviews({
    skuNumber: skuId,
    enabled: !!skuId,
    showReviewsWithImagesOnly: true,
    ...filterParams,
    select: (reviewsResponse: ReviewsResponseType) => selectImages(reviewsResponse),
  });

  return data;
}

export function prefetchReviewsWithImages(
  options: GetReviewOptions,
  queryClient: QueryClient,
  axios: AxiosInstance,
) {
  return prefetchReviews(
    {
      ...options,
      showReviewsWithImagesOnly: true,
      select: (reviewsResponse: ReviewsResponseType) => selectImages(reviewsResponse),
    },
    queryClient,
    axios,
  );
}

const selectImages = (reviewsResponse: ReviewsResponseType | null | undefined) => {
  const reviews = reviewsResponse?.productReviews;
  if (!reviews) {
    return {
      reviews: null,
      mediaItems: [],
      imageUrls: [],
      photoMap: {},
    };
  }

  const imageUrls: string[] = [];
  const photoMap: ReviewPhotoMap = {};
  const mediaItems: ReviewMediaItem[] = [];

  for (const review of reviews) {
    const reviewPhotos = review.reviewPhotos;
    const reviewVideos = review.reviewVideos;
    const hasMedia = (reviewPhotos?.length ?? 0) > 0 || (reviewVideos?.length ?? 0) > 0;

    if (!hasMedia) {
      continue;
    }

    const reviewPhotoUrls: string[] = [];

    if (reviewPhotos) {
      for (const photo of reviewPhotos) {
        const normalSizeUrl = photo.photoSizes?.find(
          (size: { photoSizeId: string }) => size.photoSizeId === 'normal',
        )?.photoUrl;

        if (normalSizeUrl && normalSizeUrl !== '') {
          imageUrls.push(normalSizeUrl);
          reviewPhotoUrls.push(normalSizeUrl);
          mediaItems.push({
            type: 'photo',
            url: normalSizeUrl,
            caption: photo.photoCaption,
          });
        }
      }
    }

    if (reviewVideos) {
      for (const video of reviewVideos) {
        const thumbnailUrl = video.videoThumbnailUrl;
        const videoUrl = video.videoUrl;

        if (thumbnailUrl && videoUrl) {
          imageUrls.push(thumbnailUrl);
          reviewPhotoUrls.push(thumbnailUrl);
          mediaItems.push({
            type: 'video',
            thumbnailUrl,
            videoUrl,
            caption: video.caption,
            videoCaptionUrl: video.videoCaptionUrl,
          });
        }
      }
    }

    if (reviewPhotoUrls.length > 0) {
      photoMap[review.reviewId] = reviewPhotoUrls;
    }
  }

  return {
    reviews,
    mediaItems,
    imageUrls,
    photoMap,
  };
};
